import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useCustomer } from "../context/CustomerContext";
import { formatDateTime } from "../utils/datetime";

/**
 * Cloud connectors — Azure, AWS, GCP and Google Workspace.
 *
 * THIS SCREEN DID NOT EXIST. The backend has had complete adapters for all
 * four platforms and forty-odd endpoints for a long time, but there was no way
 * to add a connector from the console: the only cloud page was the storage
 * browser, which lists connectors it cannot create. The generated
 * integrations reference even pointed at a "Cloud connectors" screen that was
 * never built.
 *
 * The ADD FORM IS GENERATED from /supported-platforms, whose credential field
 * list is read from each adapter's own REQUIRED_FIELDS. A hand-written form
 * asks for the wrong things the moment an adapter changes; this one cannot ask
 * for a field the adapter ignores, nor omit one it demands.
 *
 * TWO WAYS IN, and the reported bug that produced them.
 * "Adding a cloud connector should be easy and similar to the process used to
 * connect O365. Super easy to deploy with creds only and one click."
 * Worse than hard: the dialog showed a Name box and NOTHING else, because
 * /supported-platforms was rebuilding the capability payload by hand and
 * dropping credential_fields. You could press Save and store a connector with
 * no credentials in it.
 *
 *   ONE-CLICK SETUP (default) - the Microsoft 365 shape. The console mints a
 *   signed single-use token and hands over one command; the command runs in
 *   the cloud's own shell, creates a LEAST-PRIVILEGE identity, and POSTs the
 *   credentials straight back. Nothing is copied out by hand, and the scope is
 *   right by construction rather than by whatever key was to hand.
 *
 *   MANUAL - for an operator who already has a service principal or key. Now
 *   actually renders the fields, and refuses to save with them empty.
 */

type CapabilityGroup = {
  key: string;
  label: string;
  capabilities: { key: string; label: string; writes: boolean; detail: string }[];
};

type GuidedSetup = {
  run_in: string;
  grants: string[];
  manual_step: string | null;
};

type Platform = {
  platform: string;
  label: string;
  can_write: boolean;
  groups: CapabilityGroup[];
  credential_fields?: { key: string; label: string; help: string; secret: boolean }[];
  guided_setup?: GuidedSetup | null;
};

type SetupSession = {
  token: string;
  nonce: string;
  platform: string;
  filename: string;
  script_b64: string;
  guidance: GuidedSetup;
};

type Connector = {
  id: number;
  customer_id: number;
  platform: string;
  platform_label: string;
  name: string;
  enabled: boolean;
  has_credentials: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
  last_test_error: string | null;
  capabilities: Record<string, unknown> | null;
};

export default function CloudConnectors() {
  const { currentCustomerId } = useCustomer();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Cloud connectors",
    useMemo(
      () =>
        connectors.map((c: any) => ({
          platform: c.platform ?? null,
          customer: c.customer_name ?? null,
          name: c.name ?? null,
          enabled: c.enabled ?? null,
          status: c.last_status ?? null,
          last_synced_at: c.last_synced_at ?? null,
          // Status only. This screen stores tenant and API credentials and
          // none of them belongs in a model prompt.
        })),
      [connectors],
    ),
    undefined,
  );
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Connector | null>(null);

  const [form, setForm] = useState<{
    platform: string;
    name: string;
    creds: Record<string, string>;
  }>({ platform: "", name: "", creds: {} });

  // Guided ("one click") vs typing the credentials in by hand. Guided is the
  // default for a new connector because it is the only path that also gets the
  // scope right: it creates a least-privilege identity rather than inviting the
  // operator to paste whatever admin key they happen to have.
  const [mode, setMode] = useState<"guided" | "manual">("guided");
  const [setup, setSetup] = useState<SetupSession | null>(null);
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [p, c] = await Promise.all([
        api.get("/api/v1/cloud-connectors/supported-platforms"),
        api.get("/api/v1/cloud-connectors"),
      ]);
      setPlatforms(p.data || []);
      setConnectors(c.data || []);
    } catch {
      setErr("Could not load cloud connectors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (m: string) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3500);
  };

  const platformFor = (key: string) => platforms.find((p) => p.platform === key);

  const closeDialog = () => {
    setAdding(false);
    setEditing(null);
    setSetup(null);
    setShowScript(false);
    setCopied(false);
  };

  const startAdd = (platform: string) => {
    setEditing(null);
    setForm({ platform, name: "", creds: {} });
    setSetup(null);
    setShowScript(false);
    setMode(platformFor(platform)?.guided_setup ? "guided" : "manual");
    setAdding(true);
  };

  const startEdit = (c: Connector) => {
    setEditing(c);
    // Editing is always the manual form: there is nothing to provision, only
    // a name to change or a secret to rotate.
    setMode("manual");
    setSetup(null);
    // Credentials are write-only over the wire and never returned, so the
    // fields start blank: leaving them blank keeps the stored secret,
    // filling them rotates it. Saying so beats a form that looks empty by
    // accident.
    setForm({ platform: c.platform, name: c.name, creds: {} });
    setAdding(true);
  };

  const save = async () => {
    setErr("");
    setBusy("save");
    try {
      const creds = Object.fromEntries(
        Object.entries(form.creds).filter(([, v]) => (v ?? "").trim() !== ""),
      );
      if (editing) {
        await api.put(`/api/v1/cloud-connectors/${editing.id}`, {
          name: form.name.trim(),
          // Omitted when empty, so an edit that only renames does not wipe
          // the stored credential.
          ...(Object.keys(creds).length ? { credentials: creds } : {}),
        });
      } else {
        if (!currentCustomerId) {
          setErr("Pick a customer in the switcher first — a connector belongs to one.");
          setBusy(null);
          return;
        }
        await api.post("/api/v1/cloud-connectors", {
          customer_id: currentCustomerId,
          platform: form.platform,
          name: form.name.trim(),
          credentials: creds,
        });
      }
      setAdding(false);
      setEditing(null);
      await load();
      flash("Saved. Use Test to confirm the credentials work.");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(null);
    }
  };

  // ── guided setup ───────────────────────────────────────────────────────
  //
  // Same shape as connecting Microsoft 365: press the button, get one command,
  // run it where you are already signed in to that cloud. The appliance does
  // the rest and the dialog closes itself when the connector lands.

  const startGuided = async () => {
    if (!currentCustomerId) {
      setErr("Pick a customer in the switcher first — a connector belongs to one.");
      return;
    }
    setErr("");
    setBusy("guided");
    try {
      const r = await api.post("/api/v1/cloud-connectors/setup/start", {
        customer_id: currentCustomerId,
        platform: form.platform,
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
      });
      setSetup(r.data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not start setup.");
    } finally {
      setBusy(null);
    }
  };

  // Poll while the operator is off running the command. Stops on unmount, so
  // closing the dialog does not leave a timer hitting the API forever.
  useEffect(() => {
    if (!setup) return;
    let live = true;
    const tick = async () => {
      try {
        const r = await api.get("/api/v1/cloud-connectors/setup/status", {
          params: { token: setup.token },
        });
        if (!live || r.data?.status !== "done") return;
        const c = r.data.connector;
        closeDialog();
        await load();
        flash(
          c?.last_test_status === "success"
            ? `${c.platform_label} connected and tested.`
            : `${c?.platform_label || "Connector"} created, but the connection test ` +
                `failed. Open it and press Test for the reason.`,
        );
      } catch {
        /* a poll that fails is not an error the operator can act on */
      }
    };
    const id = setInterval(tick, 3000);
    return () => {
      live = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, load]);

  // The generators emit ASCII deliberately, so a plain atob is enough.
  const decodedScript = setup ? window.atob(setup.script_b64) : "";

  // Written to a FILE and then run, never piped straight into bash: the script
  // asks the operator to confirm the subscription/account, and a piped script
  // would eat its own text as the answer to that prompt.
  const oneLiner = setup
    ? `echo '${setup.script_b64}' | base64 -d > ${setup.filename} && bash ${setup.filename}`
    : "";

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(oneLiner);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setErr("Could not reach the clipboard — select the command and copy it manually.");
    }
  };

  const downloadScript = () => {
    if (!setup) return;
    const blob = new Blob([decodedScript], { type: "text/x-shellscript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = setup.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const test = async (c: Connector) => {
    setErr("");
    setBusy(`test-${c.id}`);
    try {
      const r = await api.post(`/api/v1/cloud-connectors/${c.id}/test`);
      await load();
      flash(r.data?.message || (r.data?.ok ? "Connected." : "Test failed."));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Test failed.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (c: Connector) => {
    if (
      !window.confirm(
        `Delete the connector "${c.name}"? AeroGauge loses visibility of that ` +
          `cloud; nothing in the cloud itself is touched.`,
      )
    )
      return;
    setBusy(`del-${c.id}`);
    try {
      await api.delete(`/api/v1/cloud-connectors/${c.id}`);
      await load();
      flash("Connector deleted.");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(null);
    }
  };

  const fields = platformFor(form.platform)?.credential_fields || [];
  const guided = platformFor(form.platform)?.guided_setup || null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cloud connectors</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect a customer's Azure subscription, AWS account, Google Cloud project or Google
          Workspace domain. What each one can do is decided by the platform, and shown below.
        </p>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-4 py-2 rounded-lg">
          {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-4 py-2 rounded-lg">
          {msg}
        </div>
      )}

      {/* What can be connected, and what each platform can do */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {platforms.map((p) => (
          <div
            key={p.platform}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 dark:text-gray-100">{p.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {p.groups.map((g) => g.label).join(" · ") || "no capabilities"}
                </div>
              </div>
              <button
                onClick={() => startAdd(p.platform)}
                className="shrink-0 text-xs bg-brand text-white px-2.5 py-1.5 rounded-lg hover:opacity-90"
              >
                Connect
              </button>
            </div>
            <ul className="mt-2 space-y-1">
              {p.groups.flatMap((g) =>
                g.capabilities.map((c) => (
                  <li key={`${g.key}-${c.key}`} className="text-xs flex gap-2">
                    <span
                      className={`shrink-0 mt-0.5 px-1 py-0.5 rounded font-mono text-[10px] ${
                        c.writes
                          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                          : "bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400"
                      }`}
                    >
                      {c.writes ? "write" : "read"}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300">{c.label}</span>
                  </li>
                )),
              )}
            </ul>
          </div>
        ))}
      </div>

      {/* Configured connectors */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="w-full text-sm md:min-w-[820px]">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Platform</th>
              <th className="text-left px-3 py-2 font-medium">Last test</th>
              <th className="text-left px-3 py-2 font-medium">Go to</th>
              <th className="text-left px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {connectors.map((c) => {
              const p = platformFor(c.platform);
              return (
                <tr key={c.id} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-2 font-medium">
                    {c.name}
                    {!c.enabled && (
                      <span className="ml-2 text-[11px] text-gray-500 dark:text-gray-400">
                        disabled
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{c.platform_label}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.last_test_at ? (
                      <>
                        <span
                          className={
                            c.last_test_status === "success"
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }
                        >
                          {c.last_test_status}
                        </span>{" "}
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatDateTime(c.last_test_at)}
                        </span>
                        {c.last_test_error && (
                          <div className="text-[11px] text-red-600 dark:text-red-400 max-w-[22rem] truncate">
                            {c.last_test_error}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">never tested</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {/* Only the views this platform actually supports. */}
                    {(p?.groups || []).map((g) => (
                      <Link
                        key={g.key}
                        to={`/cloud/${g.key}`}
                        className="text-brand dark:text-brand-light hover:underline mr-2"
                      >
                        {g.label}
                      </Link>
                    ))}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      onClick={() => test(c)}
                      disabled={busy === `test-${c.id}`}
                      className="text-xs text-brand dark:text-brand-light hover:underline mr-3 disabled:opacity-50"
                    >
                      {busy === `test-${c.id}` ? "Testing…" : "Test"}
                    </button>
                    <button
                      onClick={() => startEdit(c)}
                      className="text-xs text-brand dark:text-brand-light hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(c)}
                      disabled={busy === `del-${c.id}`}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && connectors.length === 0 && (
              <tr>
                <td
                  className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs"
                  colSpan={5}
                >
                  No connectors yet. Pick a platform above and press Connect.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editing
                  ? `Edit ${editing.name}`
                  : `Connect ${platformFor(form.platform)?.label}`}
              </h2>
              {!editing && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Credentials go straight into the encrypted vault and are never shown again.
                </p>
              )}
            </div>

            {/* Errors have to appear INSIDE the dialog. The page-level banner
                sits behind the overlay, so a failed action looked like a
                button that simply did nothing. */}
            {err && adding && (
              <div className="mx-5 mt-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded-lg">
                {err}
              </div>
            )}

            {/* Two ways in, and the recommended one first. */}
            {!editing && guided && (
              <div className="px-5 pt-4">
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50">
                  {(
                    [
                      ["guided", "One-click setup"],
                      ["manual", "I already have credentials"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setMode(key)}
                      className={`px-3 py-1.5 text-xs rounded-md ${
                        mode === key
                          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!editing && mode === "guided" && guided && (
              <div className="px-5 py-4 space-y-3">
                <label className="block text-xs text-gray-500 dark:text-gray-400">
                  Name <span className="text-gray-400">(optional)</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={`e.g. ${platformFor(form.platform)?.label}`}
                    disabled={!!setup}
                    className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60"
                  />
                </label>

                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 px-3 py-2.5 text-xs space-y-1.5">
                  <div className="text-gray-700 dark:text-gray-200">
                    <span className="font-medium">Run it in:</span> {guided.run_in}
                  </div>
                  <div className="text-gray-700 dark:text-gray-200">
                    <span className="font-medium">It creates, with only these rights:</span>
                    <ul className="mt-1 space-y-0.5">
                      {guided.grants.map((g) => (
                        <li key={g} className="font-mono text-[11px] text-gray-500 dark:text-gray-400">
                          {g}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {guided.manual_step && (
                    <div className="text-amber-700 dark:text-amber-300">
                      <span className="font-medium">One manual step:</span> {guided.manual_step}
                    </div>
                  )}
                </div>

                {!setup ? (
                  <>
                    {!currentCustomerId && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Pick a customer in the switcher at the top first — a connector belongs
                        to one.
                      </p>
                    )}
                    <button
                      onClick={startGuided}
                      disabled={busy === "guided" || !currentCustomerId}
                      className="w-full px-4 py-2 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {busy === "guided" ? "Preparing…" : "Generate my setup command"}
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Paste this into {guided.run_in.split("(")[0].trim()}. It creates the
                      identity and sends the credentials straight back — you will not have to
                      copy anything out.
                    </p>
                    <textarea
                      readOnly
                      rows={3}
                      value={oneLiner}
                      onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                      className="w-full border dark:border-gray-600 rounded-lg px-3 py-2 text-[11px] font-mono bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 break-all"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={copyCommand}
                        className="px-3 py-1.5 text-xs rounded-lg bg-brand text-white hover:opacity-90"
                      >
                        {copied ? "Copied" : "Copy command"}
                      </button>
                      <button
                        onClick={downloadScript}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        Download script
                      </button>
                      <button
                        onClick={() => setShowScript((s) => !s)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        {showScript ? "Hide script" : "Read the script first"}
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        Waiting for the command to run…
                      </span>
                    </div>
                    {showScript && (
                      <pre className="max-h-64 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-[11px] p-3 whitespace-pre-wrap">
                        {decodedScript}
                      </pre>
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                      The command stops working after 30 minutes. This dialog closes itself the
                      moment the connector arrives.
                    </p>
                  </div>
                )}
              </div>
            )}

            {(editing || mode === "manual" || !guided) && (
            <div className="px-5 py-4 space-y-3">
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Name
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Prod subscription"
                  className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </label>
              {!editing && fields.length === 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  This platform reported no credential fields, so there is nothing to fill in.
                  That is a fault on the appliance, not something you can work around here —
                  use one-click setup, or contact support.
                </p>
              )}
              {editing && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Leave the credential fields blank to keep the stored secret. Filling any of
                  them replaces it.
                </p>
              )}
              {fields.map((f) => (
                <label key={f.key} className="block text-xs text-gray-500 dark:text-gray-400">
                  {f.label}
                  {f.key === "service_account_json" ? (
                    <textarea
                      rows={5}
                      value={form.creds[f.key] || ""}
                      onChange={(e) =>
                        setForm({ ...form, creds: { ...form.creds, [f.key]: e.target.value } })
                      }
                      className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    <input
                      type={f.secret ? "password" : "text"}
                      autoComplete="off"
                      value={form.creds[f.key] || ""}
                      onChange={(e) =>
                        setForm({ ...form, creds: { ...form.creds, [f.key]: e.target.value } })
                      }
                      className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    />
                  )}
                  {f.help && (
                    <span className="block mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                      {f.help}
                    </span>
                  )}
                </label>
              ))}
            </div>
            )}
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={closeDialog}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                {setup ? "Close" : "Cancel"}
              </button>
              {(editing || mode === "manual" || !guided) && (
                <button
                  onClick={save}
                  disabled={
                    busy === "save" ||
                    !form.name.trim() ||
                    // Saving a new connector with an empty credential set is
                    // what the broken dialog used to let you do.
                    (!editing && !fields.every((f) => (form.creds[f.key] || "").trim()))
                  }
                  className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
                >
                  {busy === "save" ? "Saving…" : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
